var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { 
// attachProps,
camelToDashCase, createForwardRef, dashToPascalCase, } from "./utils";
import { createReactComponent } from "./createComponent";
import { marketComponentsLoaded } from "../test-helpers";
const MARKET_TAGNAME_REGEX = /(market-)(.*)/;
export const createReactOverlayComponent = (tagName, defineCustomElement) => {
    if (defineCustomElement !== undefined) {
        defineCustomElement();
    }
    const displayName = dashToPascalCase(tagName);
    const contextManagerTagName = tagName.replace(MARKET_TAGNAME_REGEX, "$1context-manager");
    const contextTagName = tagName.replace(MARKET_TAGNAME_REGEX, "$1context");
    const MarketContext = createReactComponent(contextTagName);
    const ModalElement = createReactComponent(tagName);
    // Return `MarketManagerContext` to be used used as container for Portal or
    // throw an error in case it doesn't exist
    const createOrReturnContextManager = () => {
        const contextManager = document.querySelector(contextManagerTagName);
        if (!contextManager) {
            const contextManager = document.createElement(contextManagerTagName);
            document.body.appendChild(contextManager);
            // @ts-expect-error
            return contextManager;
        }
        return contextManager;
    };
    const ReactComponent = (_a) => {
        var { children, forwardedRef, style, className, ref } = _a, cProps = __rest(_a, ["children", "forwardedRef", "style", "className", "ref"]);
        const containerRef = useRef(null);
        const contextManagerElement = createOrReturnContextManager();
        // This is a copy from react-component-lib/createOverlayComponent.tsx with proper modifications
        const propsToPass = Object.keys(cProps).reduce((acc, name) => {
            const value = cProps[name];
            if (name.indexOf("on") === 0 && name[2] === name[2].toUpperCase()) {
                acc[name] = value;
            }
            else {
                // we should only render strings, booleans, and numbers as attrs in html.
                // objects, functions, arrays etc get synced via properties on mount.
                const type = typeof value;
                if (type === "string" || type === "boolean" || type === "number") {
                    acc[camelToDashCase(name)] = value;
                }
            }
            return acc;
        }, {});
        const newProps = Object.assign(Object.assign({}, propsToPass), { ref: forwardedRef, style,
            className });
        useEffect(() => {
            const setupModal = async () => {
                var _a;
                await marketComponentsLoaded(containerRef.current);
                await ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.open(containerRef.current.firstElementChild));
                await contextManagerElement.activate();
            };
            setupModal().catch(() => null);
            return () => {
                if (contextManagerElement.childElementCount === 0) {
                    contextManagerElement.deactivate();
                }
            };
        }, []);
        if (contextManagerElement === null)
            return null;
        return createPortal(React.createElement(MarketContext, { ref: containerRef },
            React.createElement(ModalElement, Object.assign({}, newProps), children)), contextManagerElement);
    };
    ReactComponent.displayName = displayName;
    return createForwardRef(ReactComponent, displayName);
};
